/**
 * DesignationWrapper Component
 *
 * Adaptive rendering wrapper that selects between badge-only, badge+watermark,
 * or full watermark based on the UI element's size category.
 *
 * Size categories (from design doc):
 *   "inline"  (< 200px height) - Badge only
 *   "medium"  (200-500px height) - Badge in header + optional watermark
 *   "large"   (> 500px height) - Watermark overlay + badge in header
 *
 * Consumers can either:
 *   1. Specify `sizeHint` explicitly (preferred for predictable layouts)
 *   2. Let the component auto-detect size via ResizeObserver (for dynamic layouts)
 *
 * Production features (no designation) render children without any treatment.
 *
 * @see https://gitlab.com/smart-assets.io/gitlab-profile/-/blob/master/docs/designs/feature-maturity-designation-system.md
 */
import React, { useState, useRef, useEffect } from "react";
import type { FeatureDesignation } from "../../types/designation";
import DesignationBadge from "./DesignationBadge";
import DesignationWatermark from "./DesignationWatermark";

export type DesignationSizeHint = "inline" | "medium" | "large";

export interface DesignationWrapperProps {
  /** The feature designation tier. Null/undefined = production (no treatment). */
  designation?: FeatureDesignation | null;
  /** Explicit size hint. When provided, auto-detection is disabled. */
  sizeHint?: DesignationSizeHint;
  /** Child content to render */
  children: React.ReactNode;
  /** Additional CSS classes for the wrapper */
  className?: string;
}

/**
 * Determine size category from pixel height.
 * Thresholds per design doc: < 200px = inline, 200-500px = medium, > 500px = large.
 */
function getSizeFromHeight(height: number): DesignationSizeHint {
  if (height < 200) return "inline";
  if (height <= 500) return "medium";
  return "large";
}

const DesignationWrapper: React.FC<DesignationWrapperProps> = ({
  designation,
  sizeHint,
  children,
  className = "",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [detectedSize, setDetectedSize] = useState<DesignationSizeHint>("inline");

  // Auto-detect size when sizeHint is not provided
  useEffect(() => {
    if (sizeHint) return; // Skip detection when explicit hint is given

    const element = containerRef.current;
    if (!element) return;

    // Initial measurement
    setDetectedSize(getSizeFromHeight(element.offsetHeight));

    // Observe for size changes (responsive layouts)
    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const height = entry.contentRect.height;
          setDetectedSize(getSizeFromHeight(height));
        }
      });
      observer.observe(element);
      return () => observer.disconnect();
    }
  }, [sizeHint]);

  // Production features: no treatment
  if (!designation) {
    return <>{children}</>;
  }

  const effectiveSize = sizeHint || detectedSize;

  // Inline: Badge only (no watermark)
  if (effectiveSize === "inline") {
    return (
      <div
        ref={containerRef}
        data-testid={`designation-wrapper-${designation}`}
        data-size="inline"
        className={`relative ${className}`}
      >
        <div className="absolute top-1 right-1 z-10">
          <DesignationBadge designation={designation} size="sm" />
        </div>
        {children}
      </div>
    );
  }

  // Medium: Badge in header + watermark
  if (effectiveSize === "medium") {
    return (
      <div
        ref={containerRef}
        data-testid={`designation-wrapper-${designation}`}
        data-size="medium"
        className={className}
      >
        <div className="flex items-center justify-end mb-1">
          <DesignationBadge designation={designation} size="sm" />
        </div>
        <DesignationWatermark designation={designation}>{children}</DesignationWatermark>
      </div>
    );
  }

  // Large: Watermark overlay + badge in header
  return (
    <div
      ref={containerRef}
      data-testid={`designation-wrapper-${designation}`}
      data-size="large"
      className={className}
    >
      <div className="flex items-center justify-end mb-2">
        <DesignationBadge designation={designation} size="md" />
      </div>
      <DesignationWatermark designation={designation}>{children}</DesignationWatermark>
    </div>
  );
};

export default DesignationWrapper;
export { getSizeFromHeight };
